import { SlidersHorizontal, X } from "lucide-react";
import React from "react";
import { QUICK_FILTERS } from "../quickFilters";
import { Tooltip } from "./Tooltip";

const statusTextFor = (filter, active) => {
	const subject = filter.tooltipSubject || filter.label;

	if (filter.keep) {
		return active ? `${subject}: only shown` : `${subject}: included`;
	}

	return active ? `${subject}: hidden` : `${subject}: visible`;
};

// Compact round icon-only chips sitting directly on the page background.
const IconsVariant = ({ filters, onToggle, onClear, activeCount }) => (
	<div className="-my-1 flex items-center gap-1.5 overflow-x-auto overflow-y-visible py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
		<span className="mr-1 inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[var(--color-text-primary)]">
			<SlidersHorizontal className="h-4 w-4 text-[var(--color-text-tertiary)]" />
			Quick filters
		</span>

		{QUICK_FILTERS.map((f) => {
			const Icon = f.icon;
			const active = !!filters?.[f.id];
			const statusText = statusTextFor(f, active);
			return (
				<Tooltip key={f.id} content={statusText}>
					<button
						type="button"
						onClick={() => onToggle(f.id)}
						aria-label={statusText}
						aria-pressed={active}
						className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 active:scale-[0.94] transition-[background-color,color,box-shadow,transform] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50 ${
							active
								? "bg-orange-600 text-white shadow-sm ring-orange-500/60 hover:bg-orange-600/90"
								: "text-[var(--color-text-tertiary)] ring-[var(--color-border)] hover:text-[var(--color-text-primary)] hover:ring-[var(--color-text-tertiary)] hover:bg-[var(--surface-elevated-hover)]"
						}`}
					>
						<Icon className="h-3.5 w-3.5 shrink-0" />
					</button>
				</Tooltip>
			);
		})}

		{activeCount > 0 && (
			<Tooltip content={`Clear quick filters (${activeCount} active)`}>
				<button
					type="button"
					onClick={onClear}
					aria-label={`Clear quick filters (${activeCount} active)`}
					className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--color-text-tertiary)] hover:bg-red-500/10 hover:text-red-500 active:scale-[0.94] transition-[background-color,color,transform] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50"
				>
					<X className="h-3.5 w-3.5 shrink-0" />
				</button>
			</Tooltip>
		)}
	</div>
);

// Labelled pill chips housed in an elevated card.
const PillsVariant = ({ filters, onToggle, onClear, activeCount }) => (
	<div className="flex items-center gap-1.5 overflow-x-auto rounded-2xl bg-[var(--surface-elevated)] px-2.5 py-2 shadow-sm ring-1 ring-[var(--surface-elevated-ring)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
		<span className="ml-1 mr-1 inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-[var(--color-text-primary)]">
			<SlidersHorizontal className="h-3.5 w-3.5" />
			Quick filters
		</span>

		{QUICK_FILTERS.map((f) => {
			const Icon = f.icon;
			const active = !!filters?.[f.id];
			const statusText = statusTextFor(f, active);
			return (
				<Tooltip key={f.id} content={statusText}>
					<button
						type="button"
						onClick={() => onToggle(f.id)}
						aria-label={statusText}
						aria-pressed={active}
						className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium active:scale-[0.96] transition-[background-color,color,box-shadow,transform] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50 ${
							active
								? "bg-orange-600 text-white shadow-sm ring-1 ring-inset ring-orange-500/60 hover:bg-orange-600/90"
								: "bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] ring-1 ring-inset ring-[var(--color-border)] hover:ring-[var(--color-text-tertiary)] hover:brightness-110"
						}`}
					>
						<Icon className="h-4 w-4 shrink-0" />
						<span className="hidden lg:inline">{f.label}</span>
					</button>
				</Tooltip>
			);
		})}

		{activeCount > 0 && (
			<Tooltip content={`Clear quick filters (${activeCount} active)`}>
				<button
					type="button"
					onClick={onClear}
					aria-label={`Clear quick filters (${activeCount} active)`}
					className="ml-0.5 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-[var(--color-text-primary)] hover:bg-red-500/10 hover:text-red-500 active:scale-[0.96] transition-[background-color,color,transform] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50"
				>
					<X className="h-4 w-4 shrink-0" />
					<span className="hidden sm:inline">Clear</span>
				</button>
			</Tooltip>
		)}
	</div>
);

export const QuickFilters = ({ filters, onToggle, onClear, variant }) => {
	const activeCount = QUICK_FILTERS.filter((f) => filters?.[f.id]).length;

	const Variant = variant === "pills" ? PillsVariant : IconsVariant;

	return (
		<Variant
			filters={filters}
			onToggle={onToggle}
			onClear={onClear}
			activeCount={activeCount}
		/>
	);
};
